import type { Action } from './actions';
import { mapInput } from './mapper';

/** Keys that would scroll the page if the game did not take them. */
const PREVENT_DEFAULT = new Set(['Space', 'ArrowUp']);

/**
 * Binds keyboard and Pointer Events to an action sink: the action queue, or the pause router of
 * src/game/pause.ts that handles PAUSE and drops flaps while paused. Only Pointer Events are used (no mousedown or
 * touchstart), so a tap on mobile never produces a second, synthesized flap.
 * Returns a function that removes the listeners.
 */
export function bindDomInput(queue: { enqueue(action: Action): void }, canvas: HTMLElement, target: Window = window): () => void {
  const onKeyDown = (event: KeyboardEvent) => {
    const action = mapInput({ kind: 'keydown', code: event.code, repeat: event.repeat });
    if (PREVENT_DEFAULT.has(event.code)) event.preventDefault();
    if (action) queue.enqueue(action);
  };
  const onPointerDown = (event: PointerEvent) => {
    const action = mapInput({
      kind: 'pointerdown',
      pointerType: event.pointerType,
      button: event.button,
      target: event.target === canvas ? 'canvas' : 'ui',
    });
    if (action) queue.enqueue(action);
  };

  target.addEventListener('keydown', onKeyDown);
  target.addEventListener('pointerdown', onPointerDown);
  return () => {
    target.removeEventListener('keydown', onKeyDown);
    target.removeEventListener('pointerdown', onPointerDown);
  };
}
